import type { ElementType } from 'react'

interface TechnologyCardProps {
  technology: {
    name: string
    category?: string
    description?: string
    experience?: string
    level?: string
    icon?: string
    // Legacy/static shape support
    Icon?: ElementType
    color?: string
    proficiency?: number
  }
}

export default function TechnologyCard({ technology }: TechnologyCardProps) {
  const IconComponent = technology.Icon

  const proficiency = (() => {
    if (typeof technology.proficiency === 'number' && Number.isFinite(technology.proficiency)) {
      return Math.max(0, Math.min(100, Math.round(technology.proficiency)))
    }

    const level = (technology.level ?? '').toLowerCase()
    if (level.includes('expert')) return 95
    if (level.includes('advanced')) return 85
    if (level.includes('intermediate')) return 70
    if (level.includes('beginner')) return 50

    return 75
  })()

  const iconLabel = typeof technology.icon === 'string' && technology.icon.trim()
    ? technology.icon.trim()
    : '◦'

  return (
    <div className="card-studio flex flex-col justify-between h-full p-6">
      <div>
        {/* Icon and Name */}
        <div className="flex items-center gap-3 mb-4">
          <div
            className="text-2xl flex items-center justify-center w-10 h-10 rounded-lg border"
            style={{
              background: 'var(--surface-2)',
              borderColor: 'var(--border)',
              color: 'var(--text)'
            }}
          >
            {IconComponent ? <IconComponent /> : <span aria-hidden>{iconLabel}</span>}
          </div>
          <h3
            className="font-display text-[1.12rem] font-semibold"
            style={{ color: 'var(--text)', letterSpacing: '-0.01em' }}
          >
            {technology.name}
          </h3>
        </div>

        {/* Description */}
        <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--muted)' }}>
          {technology.description ?? ''}
        </p>
      </div>

      {/* Proficiency Bar */}
      <div className="space-y-2 mt-auto">
        <div className="flex justify-between items-center text-xs font-mono" style={{ color: 'var(--muted)' }}>
          <span>Proficiency</span>
          <span>{proficiency}%</span>
        </div>
        <div
          className="w-full rounded-full h-1.5 overflow-hidden"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}
        >
          <div
            className="h-full transition-all duration-1000"
            style={{
              width: `${proficiency}%`,
              background: 'var(--accent)',
            }}
          />
        </div>
      </div>
    </div>
  )
}